import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, MapPin } from "lucide-react";
import { ListCard, ActionButton, EmptyState, LoadingState, ScrollableList, ConfirmDialog } from "../../components/UILibrary";
import { UNIFIED_THEME } from "../../styles/unifiedTheme";
import { useToast } from "../../hooks/useToast";
import ToastContainer from "../../components/ToastContainer";
import { getErrorMessage } from "../../utils/errorHandler";

export default function CitiesPage({ theme = UNIFIED_THEME }) {
  const toast = useToast();
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // ✅ Helper: Get token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  // ✅ Fetch all countries once
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-countries`,
          { headers: getAuthHeaders() }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setCountries(data.countries || []);
      } catch (error) {
        console.error("Error fetching countries:", error);
        const errorMsg = getErrorMessage(error, 'Failed to load countries');
        toast.error(errorMsg);
        setCountries([]);
      }
    };

    fetchCountries();
  }, []);

  // ✅ Fetch all cities once and filter client-side for better UX
  const fetchAllCities = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/all-cities`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setCities(data.cities || []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching cities:", error);
      const errorMsg = getErrorMessage(error, 'Failed to load cities');
      toast.error(errorMsg);
      setCities([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Load all cities on first render
  useEffect(() => {
    fetchAllCities();
  }, []);

  // ✅ Delete city with proper error handling
  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const deletingCity = cities.find(c => c._id === id);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/delete-cities/${id}`,
        { method: "DELETE", headers: getAuthHeaders() }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to delete city: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setCities((prev) => prev.filter((city) => city._id !== id));
        const cityName = deletingCity?.city_name || 'City';
        toast.success(`${cityName} deleted successfully!`);
        setCurrentPage(1);
      } else {
        throw new Error(data.message || 'Failed to delete city');
      }
    } catch (error) {
      console.error("Error deleting city:", error);
      const errorMsg = getErrorMessage(error, 'Failed to delete city');
      toast.error(errorMsg);
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  // ✅ Combined filtering logic: search + country filter
  const filteredCities = useMemo(() => {
    let result = cities;
    
    // Filter by country if selected
    if (selectedCountry) {
      result = result.filter(city => city.country_id === parseInt(selectedCountry));
    }
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(city =>
        city.city_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return result;
  }, [cities, selectedCountry, searchTerm]);

  // ✅ Pagination logic
  const itemsToShow = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredCities.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredCities, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCities.length / itemsPerPage);

  // ✅ Get country name by ID
  const getCountryName = (countryId) => {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : 'Unknown';
  };

  return (
    <>
      <ToastContainer toasts={toast.toasts} onRemove={toast.remove} />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="space-y-6 p-4"
      >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl font-bold text-gray-800 mb-2">City Management</h1>
        <p className="text-gray-600 text-sm">Manage cities across different countries</p>
      </motion.div>

      {/* Search and Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search City</label>
            <input
              type="text"
              placeholder="Enter city name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
            />
          </div>

          {/* Country Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
            <select
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
            >
              <option value="">All Countries</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
          <p className="text-blue-600 text-xs font-semibold uppercase mb-1">Total Cities</p>
          <p className="text-2xl font-bold text-blue-900">{cities.length}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
          <p className="text-blue-600 text-xs font-semibold uppercase mb-1">Filtered Results</p>
          <p className="text-2xl font-bold text-blue-900">{filteredCities.length}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
          <p className="text-purple-600 text-xs font-semibold uppercase mb-1">Total Countries</p>
          <p className="text-2xl font-bold text-purple-900">{countries.length}</p>
        </div>
      </div>

      {/* Cities List */}
      {isLoading ? (
        <LoadingState />
      ) : filteredCities.length === 0 ? (
        <EmptyState
          icon={MapPin}
          title="No cities found"
          description={searchTerm || selectedCountry ? "Try adjusting your search criteria" : "No cities available"}
        />
      ) : (
        <ScrollableList maxHeight="600px">
          <div className="space-y-2">
            <AnimatePresence>
              {itemsToShow.map((city) => (
                <motion.div
                  key={city.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <ListCard
                    avatar={city.city_name[0]?.toUpperCase() || "C"}
                    title={city.city_name}
                    subtitle={getCountryName(city.country_id)}
                    actions={
                      <ActionButton
                        icon={Trash2}
                        onClick={() => setConfirmDelete(city.id)}
                        variant="danger"
                        size="sm"
                      />
                    }
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </ScrollableList>
      )}

      {/* Pagination */}
      {!isLoading && filteredCities.length > itemsPerPage && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalPages }).map((_, i) => (
            <motion.button
              key={i + 1}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentPage(i + 1)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                currentPage === i + 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {i + 1}
            </motion.button>
          ))}
        </div>
      )}

      {/* Summary */}
      {filteredCities.length > 0 && (
        <p className="text-sm text-gray-600 text-center">
          Showing <span className="font-semibold">{itemsToShow.length}</span> of{" "}
          <span className="font-semibold">{filteredCities.length}</span> cities
          {searchTerm && <> matching "{searchTerm}"</>
          }
        </p>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDelete !== null}
        title="Delete City"
        message="Are you sure you want to delete this city? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        onConfirm={() => {
          if (confirmDelete) handleDelete(confirmDelete);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
      </motion.div>
    </>
  );
}